// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { FreebetsIcon } from '../../../assets/img/betting';
import { Target } from '../../../client';
import { __ } from '../../../i18n/l10n';
import MPGetFreebetsButtonBackgroundSvgAsComponent from '../assets/svg/asComponent/MPGetFreebetsButtonBackgroundAsComponent';
import MPBannerUserProgressContainer from '../containers/MPBannerUserProgressContainer';
import { MPBannerStatuses, type MPBannerStatus } from '../types/banner';

import MPPrimaryButton from './MPPrimaryButton';

const Wrapper = styled.div`
  position: relative;

  ${!Target.__MOBILE__
    ? css`
        transition: filter 200ms ease-in-out;

        &:hover {
          filter: brightness(1.1);
        }
      `
    : undefined};

  ${({ onClick }) => {
    if (onClick) {
      return css`
        cursor: pointer;
      `;
    }
  }}
`;

const Background = styled.img`
  border-radius: 0.6em;
  width: 100%;
  padding: 0;
  margin: 0;
`;

const appearanceAnimation = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const topRightPlacementSharedStyle = css`
  position: absolute;
  right: 1em;
  top: 0.5em;
  z-index: 1;
`;

const StyledMPBannerUserProgressContainer = styled(MPBannerUserProgressContainer)`
  ${topRightPlacementSharedStyle}

  right: 2.1em;
  top: 1.2em;
  font-size: 0.75em;
  animation: ${appearanceAnimation} 300ms ease-in-out forwards;
`;

const StartButton = styled(MPPrimaryButton)`
  ${topRightPlacementSharedStyle}

  font-size: 0.675em;
`;

const GET_FREEBETS_BUTTON_ANIMATION_DURATION = 3000; // ms

const bounceAnimation = keyframes`
  40% {
    transform: scale(1);
  }

  43% {
    transform: scale(1.05);
  }

  46% {
    transform: scale(1);
  }

  54% {
    transform: scale(1);
  }

  57% {
    transform: scale(1.05);
  }

  60% {
    transform: scale(1);
  }
`;

const GetFreebetsButton = styled.div`
  ${topRightPlacementSharedStyle}

  margin-right: -0.1em;
  padding: 0.8em;
  font-size: 0.8em;
  overflow: hidden;
  transition: filter 150ms ease-in-out;

  user-select: none;

  animation: ${bounceAnimation} ${GET_FREEBETS_BUTTON_ANIMATION_DURATION}ms ease-in-out infinite;

  ${!Target.__MOBILE__
    ? css`
        &:hover {
          filter: brightness(105%);
        }
      `
    : undefined}

  &:active {
    filter: brightness(110%);
  }
`;

const glowAnimation = keyframes`
  40% {
    filter: brightness(100%);
  }

  43% {
    filter: brightness(120%);
  }

  46% {
    filter: brightness(100%);
  }

  54% {
    filter: brightness(100%);
  }

  57% {
    filter: brightness(120%);
  }

  60% {
    filter: brightness(100%);
  }
`;

const GetFreebetsButtonBackground = styled(MPGetFreebetsButtonBackgroundSvgAsComponent)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;

  animation: ${glowAnimation} ${GET_FREEBETS_BUTTON_ANIMATION_DURATION}ms ease-in-out infinite;
`;

const GetFreebetsButtonLabelWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GetFreebetsButtonLabel = styled.div`
  color: #8a4d0f;
  text-transform: uppercase;
  font-weight: bold;
`;

const FreebetIcon = styled.img`
  margin-left: 0.2em;
  width: 1em;
  height: 1em;
`;

const emptyOnClick = () => {};

type MPBannerProps = {
  className?: string;
  status: MPBannerStatus;
  backgroundPath: string;
  onClick?: () => void;
};

const MPBanner = ({ className, status, backgroundPath, onClick }: MPBannerProps) => {
  const content = useMemo(() => {
    if (status === MPBannerStatuses.FREEBETS_AVAILABLE) {
      return (
        <GetFreebetsButton>
          <GetFreebetsButtonBackground />
          <GetFreebetsButtonLabelWrapper>
            <GetFreebetsButtonLabel>{__('Claim')}</GetFreebetsButtonLabel>
            <FreebetIcon src={FreebetsIcon} alt="freebet icon" />
          </GetFreebetsButtonLabelWrapper>
        </GetFreebetsButton>
      );
    }

    if (status === MPBannerStatuses.NOT_OPT_IN_YET) {
      return <StartButton onClick={emptyOnClick}>{__('Start')}</StartButton>;
    }

    if (status === MPBannerStatuses.PLAYING) {
      return <StyledMPBannerUserProgressContainer />;
    }
  }, [status]);

  return (
    <Wrapper className={className} onClick={onClick}>
      <Background src={backgroundPath} />
      {content}
    </Wrapper>
  );
};

export default MPBanner;
